import { useState, useEffect } from 'react';
import { FileText, Download, Mail, Loader2, Search, ArrowRightLeft, X, Eye, Ban, CheckCircle, AlertTriangle, Plus, Clock } from 'lucide-react';
import { adminApi } from '../../services/api';
import JSZip from 'jszip';

interface PendingSubscription {
  id: string;
  quantity: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  plan: { id: string; displayName: string; priceYearly: number; currency: string };
  tenant: { id: string; name: string; companyName: string | null; vatRate: number | null };
}

interface ExpiringSubscription {
  id: string;
  name: string | null;
  quantity: number;
  expiresAt: string;
  daysUntilExpiry: number;
  plan: { id: string; displayName: string; priceYearly: number; currency: string };
  tenant: { id: string; name: string; companyName: string | null; vatRate: number | null };
}

interface Invoice {
  id: string;
  invoiceNumber: string | null;
  amountDue: number;
  status: string;
  description: string | null;
  createdAt: string;
  dueDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  subscription: {
    id: string;
    quantity: number;
    tenant: {
      id: string;
      name: string;
      slug: string;
      companyName: string | null;
    };
    plan: {
      id: string;
      name: string;
      displayName: string;
    };
  };
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [refusingId, setRefusingId] = useState<string | null>(null);
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);

  // Multi-selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkRefusing, setBulkRefusing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  // Pending invoices
  const [pendingSubs, setPendingSubs] = useState<PendingSubscription[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [generatingAll, setGeneratingAll] = useState(false);

  // Expiring soon
  const [expiringSubs, setExpiringSubs] = useState<ExpiringSubscription[]>([]);
  const [expiringCount, setExpiringCount] = useState(0);
  const [showExpiringModal, setShowExpiringModal] = useState(false);

  // PDF Viewer state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfInvoice, setPdfInvoice] = useState<Invoice | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const loadPendingInvoices = async () => {
    try {
      const res = await adminApi.getPendingInvoices();
      setPendingSubs(res.data?.pending || []);
      setPendingCount(res.data?.count || 0);
    } catch {
      // silently ignore
    }
  };

  const loadExpiringSoon = async () => {
    try {
      const res = await adminApi.getExpiringSoonInvoices(30);
      setExpiringSubs(res.data?.expiring || []);
      setExpiringCount(res.data?.count || 0);
    } catch {
      // silently ignore
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [statusFilter, page]);

  useEffect(() => {
    loadPendingInvoices();
    loadExpiringSoon();
    const interval = setInterval(() => { loadPendingInvoices(); loadExpiringSoon(); }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Cleanup blob URL on unmount or when closing
  useEffect(() => {
    return () => {
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Reset selection on page/filter change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, statusFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getInvoices({
        status: statusFilter || undefined,
        page,
        limit,
      });
      setInvoices(res.data?.invoices || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      console.error('Erreur chargement factures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoiceId: string, invoiceNumber: string | null) => {
    try {
      const res = await adminApi.downloadInvoice(invoiceId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber || invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement facture:', err);
    }
  };

  const handlePreview = async (inv: Invoice) => {
    setLoadingPdf(true);
    setPdfInvoice(inv);
    try {
      const res = await adminApi.downloadInvoice(inv.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('Erreur chargement PDF:', err);
      setPdfInvoice(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  const closePdfViewer = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfInvoice(null);
  };

  const handleSend = async (invoiceId: string) => {
    setSendingId(invoiceId);
    try {
      await adminApi.sendInvoice(invoiceId);
    } catch (err) {
      console.error('Erreur envoi facture:', err);
    } finally {
      setSendingId(null);
    }
  };

  const handleConvert = async (invoiceId: string) => {
    if (!confirm('Convertir cette proforma en facture définitive ? Cette action est irréversible.')) return;
    setConvertingId(invoiceId);
    try {
      await adminApi.convertInvoice(invoiceId);
      loadInvoices();
    } catch (err) {
      console.error('Erreur conversion facture:', err);
    } finally {
      setConvertingId(null);
    }
  };

  const handleRefuse = async (invoiceId: string) => {
    if (!confirm('Refuser cette proforma ? L\'abonnement ne sera pas renouvelé.')) return;
    setRefusingId(invoiceId);
    try {
      await adminApi.refuseInvoice(invoiceId);
      loadInvoices();
      if (pdfInvoice?.id === invoiceId) closePdfViewer();
    } catch (err) {
      console.error('Erreur refus facture:', err);
    } finally {
      setRefusingId(null);
    }
  };

  const handleGeneratePending = async (subscriptionId: string) => {
    setGeneratingId(subscriptionId);
    try {
      await adminApi.generateInvoice(subscriptionId);
      await Promise.all([loadInvoices(), loadPendingInvoices()]);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Erreur lors de la génération';
      alert(msg);
    } finally {
      setGeneratingId(null);
    }
  };

  const handleGenerateAll = async () => {
    if (!confirm(`Générer ${pendingSubs.length} facture(s) pour tous les abonnements en attente ?`)) return;
    setGeneratingAll(true);
    let generated = 0;
    let errors = 0;
    for (const sub of pendingSubs) {
      try {
        await adminApi.generateInvoice(sub.id);
        generated++;
      } catch {
        errors++;
      }
    }
    setGeneratingAll(false);
    await Promise.all([loadInvoices(), loadPendingInvoices()]);
    alert(`${generated} facture(s) générée(s)${errors > 0 ? `, ${errors} erreur(s)` : ''}`);
  };

  const handleMarkPaid = async (invoiceId: string) => {
    if (!confirm('Marquer cette facture comme payée ?')) return;
    setMarkingPaidId(invoiceId);
    try {
      await adminApi.markInvoicePaid(invoiceId);
      loadInvoices();
    } catch (err) {
      console.error('Erreur marquage payée:', err);
    } finally {
      setMarkingPaidId(null);
    }
  };

  // --- Multi-selection helpers ---
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredInvoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredInvoices.map((inv) => inv.id)));
    }
  };

  const handleBulkDownload = async () => {
    const selected = filteredInvoices.filter((inv) => selectedIds.has(inv.id));
    if (selected.length === 0) return;

    // Single file: download directly
    if (selected.length === 1) {
      await handleDownload(selected[0].id, selected[0].invoiceNumber);
      return;
    }

    setBulkDownloading(true);
    setBulkProgress({ current: 0, total: selected.length });

    try {
      const zip = new JSZip();

      for (let i = 0; i < selected.length; i++) {
        const inv = selected[i];
        setBulkProgress({ current: i + 1, total: selected.length });
        try {
          const res = await adminApi.downloadInvoice(inv.id);
          const filename = `${inv.invoiceNumber || inv.id}.pdf`;
          zip.file(filename, res.data);
        } catch (err) {
          console.error(`Erreur téléchargement ${inv.invoiceNumber}:`, err);
        }
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factures_${new Date().toISOString().split('T')[0]}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement groupé:', err);
    } finally {
      setBulkDownloading(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  };

  const handleBulkSend = async () => {
    const selected = filteredInvoices.filter((inv) => selectedIds.has(inv.id));
    if (selected.length === 0) return;
    if (!confirm(`Envoyer ${selected.length} facture(s) par email aux clients respectifs ?`)) return;

    setBulkSending(true);
    setBulkProgress({ current: 0, total: selected.length });

    let sent = 0;
    let errors = 0;
    for (let i = 0; i < selected.length; i++) {
      setBulkProgress({ current: i + 1, total: selected.length });
      try {
        await adminApi.sendInvoice(selected[i].id);
        sent++;
      } catch {
        errors++;
      }
    }

    setBulkSending(false);
    setBulkProgress({ current: 0, total: 0 });
    alert(`${sent} email(s) envoyé(s)${errors > 0 ? `, ${errors} erreur(s)` : ''}`);
  };

  const handleBulkRefuse = async () => {
    const selected = filteredInvoices.filter((inv) => selectedIds.has(inv.id) && isProforma(inv));
    if (selected.length === 0) {
      alert('Aucune proforma sélectionnée à refuser.');
      return;
    }
    if (!confirm(`Refuser ${selected.length} proforma(s) ? Les abonnements concernés ne seront pas renouvelés.`)) return;

    setBulkRefusing(true);
    setBulkProgress({ current: 0, total: selected.length });

    let refused = 0;
    let errors = 0;
    for (let i = 0; i < selected.length; i++) {
      setBulkProgress({ current: i + 1, total: selected.length });
      try {
        await adminApi.refuseInvoice(selected[i].id);
        refused++;
      } catch {
        errors++;
      }
    }

    setBulkRefusing(false);
    setBulkProgress({ current: 0, total: 0 });
    setSelectedIds(new Set());
    loadInvoices();
    alert(`${refused} proforma(s) refusée(s)${errors > 0 ? `, ${errors} erreur(s)` : ''}`);
  };

  const handleBulkMarkPaid = async () => {
    const selected = filteredInvoices.filter((inv) => selectedIds.has(inv.id) && inv.status !== 'PAID' && inv.status !== 'VOID');
    if (selected.length === 0) {
      alert('Aucune facture éligible sélectionnée.');
      return;
    }
    if (!confirm(`Marquer ${selected.length} facture(s) comme payée(s) ?`)) return;

    setBulkSending(true); // reuse bulkSending state for loading
    setBulkProgress({ current: 0, total: selected.length });

    let paid = 0;
    let errors = 0;
    for (let i = 0; i < selected.length; i++) {
      setBulkProgress({ current: i + 1, total: selected.length });
      try {
        await adminApi.markInvoicePaid(selected[i].id);
        paid++;
      } catch {
        errors++;
      }
    }

    setBulkSending(false);
    setBulkProgress({ current: 0, total: 0 });
    setSelectedIds(new Set());
    loadInvoices();
    alert(`${paid} facture(s) marquée(s) comme payée(s)${errors > 0 ? `, ${errors} erreur(s)` : ''}`);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      DRAFT: 'bg-gray-100 text-gray-500',
      OPEN: 'bg-blue-500/20 text-blue-400',
      PAID: 'bg-green-500/20 text-green-400',
      VOID: 'bg-red-500/20 text-red-400',
      UNCOLLECTIBLE: 'bg-orange-500/20 text-orange-400',
    };
    const labels: Record<string, string> = {
      DRAFT: 'Proforma',
      OPEN: 'Ouverte',
      PAID: 'Payée',
      VOID: 'Annulée',
      UNCOLLECTIBLE: 'Irrécouvrable',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const isProforma = (inv: Invoice) => {
    return inv.invoiceNumber?.startsWith('PRO-') || (!inv.invoiceNumber?.startsWith('INV-') && inv.status === 'DRAFT');
  };

  // Filter by search query (client-side)
  const filteredInvoices = searchQuery
    ? invoices.filter((inv) => {
        const q = searchQuery.toLowerCase();
        return (
          (inv.invoiceNumber || '').toLowerCase().includes(q) ||
          inv.subscription.tenant.name.toLowerCase().includes(q) ||
          (inv.subscription.tenant.companyName || '').toLowerCase().includes(q) ||
          inv.subscription.plan.displayName.toLowerCase().includes(q)
        );
      })
    : invoices;

  if (loading && invoices.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  const totalPages = Math.ceil(total / limit);
  const bulkInProgress = bulkDownloading || bulkSending || bulkRefusing;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Factures</h1>
        <p className="text-gray-500">Liste de toutes les factures et proformas</p>
      </div>

      {/* Pending Invoices Banner */}
      {pendingCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-orange-800">
                {pendingCount} abonnement{pendingCount > 1 ? 's' : ''} renouvelé{pendingCount > 1 ? 's' : ''} sans facture
              </p>
              <p className="text-xs text-orange-600">
                Des factures doivent être générées manuellement pour ces abonnements.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowPendingModal(true)}
            className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
            Générer les factures
          </button>
        </div>
      )}

      {/* Pending Invoices Modal */}
      {showPendingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Factures en attente</h3>
                  <p className="text-sm text-gray-500">{pendingSubs.length} abonnement(s) nécessitent une facture</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {pendingSubs.length > 1 && (
                  <button
                    onClick={handleGenerateAll}
                    disabled={generatingAll || generatingId !== null}
                    className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                  >
                    {generatingAll ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    {generatingAll ? 'Génération...' : 'Tout générer'}
                  </button>
                )}
                <button
                  onClick={() => setShowPendingModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {pendingSubs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-400" />
                  <p>Toutes les factures sont à jour !</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingSubs.map((sub) => {
                    const vatRate = sub.tenant.vatRate ?? 17;
                    const priceHT = (sub.quantity || 1) * sub.plan.priceYearly;
                    const amountTTC = priceHT * (1 + vatRate / 100);
                    return (
                      <div
                        key={sub.id}
                        className="bg-gray-50 rounded-lg p-4 flex items-center justify-between border border-gray-200"
                      >
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">
                            {sub.tenant.companyName || sub.tenant.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {sub.plan.displayName} — {sub.quantity || 1} véhicule{(sub.quantity || 1) > 1 ? 's' : ''}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Période : {new Date(sub.currentPeriodStart).toLocaleDateString('fr-FR')} — {new Date(sub.currentPeriodEnd).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">{amountTTC.toFixed(2)} €</p>
                            <p className="text-xs text-gray-400">TTC ({vatRate}%)</p>
                          </div>
                          <button
                            onClick={() => handleGeneratePending(sub.id)}
                            disabled={generatingId === sub.id || generatingAll}
                            className="px-3 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                          >
                            {generatingId === sub.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <FileText className="h-4 w-4" />
                            )}
                            Générer
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expiring Soon Banner */}
      {expiringCount > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">
                {expiringCount} abonnement{expiringCount > 1 ? 's' : ''} expire{expiringCount > 1 ? 'nt' : ''} dans les 30 prochains jours
              </p>
              <p className="text-xs text-green-600">
                Ces abonnements arrivent bientôt à échéance.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowExpiringModal(true)}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            <Eye className="h-4 w-4" />
            Voir les détails
          </button>
        </div>
      )}

      {/* Expiring Soon Modal */}
      {showExpiringModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-green-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Abonnements expirant bientôt</h3>
                  <p className="text-sm text-gray-500">{expiringSubs.length} abonnement(s) dans les 30 prochains jours</p>
                </div>
              </div>
              <button
                onClick={() => setShowExpiringModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {expiringSubs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-400" />
                  <p>Aucun abonnement n'expire dans les 30 prochains jours.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {expiringSubs.map((sub) => (
                    <div
                      key={sub.id}
                      className="bg-gray-50 rounded-lg p-4 flex items-center justify-between border border-gray-200"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {sub.tenant.companyName || sub.tenant.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {sub.plan.displayName} — {sub.quantity || 1} véhicule{(sub.quantity || 1) > 1 ? 's' : ''}
                        </p>
                        {sub.name && (
                          <p className="text-xs text-gray-400">{sub.name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          {new Date(sub.expiresAt).toLocaleDateString('fr-FR')}
                        </p>
                        <p className={`text-xs font-medium ${sub.daysUntilExpiry <= 7 ? 'text-red-500' : sub.daysUntilExpiry <= 15 ? 'text-orange-500' : 'text-green-600'}`}>
                          {sub.daysUntilExpiry} jour{sub.daysUntilExpiry > 1 ? 's' : ''} restant{sub.daysUntilExpiry > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Rechercher par numéro, client, plan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 pl-10"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="">Tous les statuts</option>
            <option value="DRAFT">Proforma</option>
            <option value="OPEN">Ouverte</option>
            <option value="PAID">Payée</option>
            <option value="VOID">Annulée</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm font-medium text-cyan-800">
            {selectedIds.size} facture{selectedIds.size > 1 ? 's' : ''} sélectionnée{selectedIds.size > 1 ? 's' : ''}
            {bulkInProgress && bulkProgress.total > 0 && (
              <span className="ml-2 text-cyan-600">
                ({bulkProgress.current}/{bulkProgress.total})
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkDownload}
              disabled={bulkInProgress}
              className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              {bulkDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {bulkDownloading ? 'Téléchargement...' : 'Télécharger'}
            </button>
            <button
              onClick={handleBulkSend}
              disabled={bulkInProgress}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              {bulkSending && !bulkRefusing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              Envoyer par email
            </button>
            <button
              onClick={handleBulkMarkPaid}
              disabled={bulkInProgress}
              className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              <CheckCircle className="h-4 w-4" />
              Marquer payée
            </button>
            <button
              onClick={handleBulkRefuse}
              disabled={bulkInProgress}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              {bulkRefusing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
              {bulkRefusing ? 'Refus...' : 'Refuser proformas'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkInProgress}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="w-10 p-4">
                <input
                  type="checkbox"
                  checked={filteredInvoices.length > 0 && selectedIds.size === filteredInvoices.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </th>
              <th className="text-left p-4 text-gray-500 font-medium">Numéro</th>
              <th className="text-left p-4 text-gray-500 font-medium">Client</th>
              <th className="text-left p-4 text-gray-500 font-medium">Abonnement</th>
              <th className="text-right p-4 text-gray-500 font-medium">Montant TTC</th>
              <th className="text-left p-4 text-gray-500 font-medium">Statut</th>
              <th className="text-left p-4 text-gray-500 font-medium">Date</th>
              <th className="text-left p-4 text-gray-500 font-medium">Échéance</th>
              <th className="text-center p-4 text-gray-500 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((inv) => (
              <tr
                key={inv.id}
                className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedIds.has(inv.id) ? 'bg-cyan-50/50' : ''}`}
                onClick={() => handlePreview(inv)}
              >
                <td className="w-10 p-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(inv.id)}
                    onChange={() => toggleSelect(inv.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-900 text-sm">
                      {inv.invoiceNumber || '-'}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div>
                    <p className="text-gray-900 font-medium text-sm">
                      {inv.subscription.tenant.companyName || inv.subscription.tenant.name}
                    </p>
                    <p className="text-gray-400 text-xs">{inv.subscription.tenant.slug}</p>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-gray-700">{inv.subscription.plan.displayName}</span>
                </td>
                <td className="p-4 text-right">
                  <span className="font-semibold text-gray-900">{inv.amountDue.toFixed(2)} €</span>
                </td>
                <td className="p-4">{getStatusBadge(inv.status)}</td>
                <td className="p-4 text-sm text-gray-600">
                  {new Date(inv.createdAt).toLocaleDateString('fr-FR')}
                </td>
                <td className="p-4 text-sm text-gray-600">
                  {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('fr-FR') : '-'}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handlePreview(inv)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-purple-500 transition-colors"
                      title="Voir le PDF"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDownload(inv.id, inv.invoiceNumber)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-cyan-500 transition-colors"
                      title="Télécharger PDF"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleSend(inv.id)}
                      disabled={sendingId === inv.id}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-green-500 transition-colors"
                      title="Envoyer par email"
                    >
                      {sendingId === inv.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Mail className="h-4 w-4" />
                      )}
                    </button>
                    {inv.status !== 'PAID' && inv.status !== 'VOID' && (
                      <button
                        onClick={() => handleMarkPaid(inv.id)}
                        disabled={markingPaidId === inv.id}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-emerald-500 transition-colors"
                        title="Marquer comme payée"
                      >
                        {markingPaidId === inv.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    {isProforma(inv) && (
                      <>
                        <button
                          onClick={() => handleConvert(inv.id)}
                          disabled={convertingId === inv.id}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-amber-500 transition-colors"
                          title="Convertir en facture définitive (INV-)"
                        >
                          {convertingId === inv.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ArrowRightLeft className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRefuse(inv.id)}
                          disabled={refusingId === inv.id}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-red-500 transition-colors"
                          title="Refuser (annuler le renouvellement)"
                        >
                          {refusingId === inv.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Ban className="h-4 w-4" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredInvoices.length === 0 && (
          <div className="text-center py-12 text-gray-500">Aucune facture trouvée</div>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-gray-500 text-sm">
            {(page - 1) * limit + 1}-{Math.min(page * limit, total)} sur {total} factures
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Préc.
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (page <= 4) {
                pageNum = i + 1;
              } else if (page >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = page - 3 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    pageNum === page
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suiv.
            </button>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {(pdfInvoice || loadingPdf) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-cyan-500" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {pdfInvoice?.invoiceNumber || 'Facture'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {pdfInvoice?.subscription.tenant.companyName || pdfInvoice?.subscription.tenant.name}
                    {' - '}
                    {pdfInvoice?.subscription.plan.displayName}
                    {' - '}
                    {pdfInvoice?.amountDue.toFixed(2)} €
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {pdfInvoice && (
                  <>
                    <button
                      onClick={() => handleDownload(pdfInvoice.id, pdfInvoice.invoiceNumber)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-cyan-500 transition-colors"
                      title="Télécharger"
                    >
                      <Download className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleSend(pdfInvoice.id)}
                      disabled={sendingId === pdfInvoice.id}
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-green-500 transition-colors"
                      title="Envoyer par email"
                    >
                      {sendingId === pdfInvoice.id ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Mail className="h-5 w-5" />
                      )}
                    </button>
                    {pdfInvoice.status !== 'PAID' && pdfInvoice.status !== 'VOID' && (
                      <button
                        onClick={() => {
                          handleMarkPaid(pdfInvoice.id);
                          closePdfViewer();
                        }}
                        disabled={markingPaidId === pdfInvoice.id}
                        className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-1"
                        title="Marquer comme payée"
                      >
                        {markingPaidId === pdfInvoice.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                        Payée
                      </button>
                    )}
                    {isProforma(pdfInvoice) && (
                      <>
                        <button
                          onClick={() => {
                            handleConvert(pdfInvoice.id);
                            closePdfViewer();
                          }}
                          disabled={convertingId === pdfInvoice.id}
                          className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center gap-1"
                          title="Convertir en facture définitive"
                        >
                          {convertingId === pdfInvoice.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ArrowRightLeft className="h-4 w-4" />
                          )}
                          Convertir en facture
                        </button>
                        <button
                          onClick={() => handleRefuse(pdfInvoice.id)}
                          disabled={refusingId === pdfInvoice.id}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-1"
                          title="Refuser le renouvellement"
                        >
                          {refusingId === pdfInvoice.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Ban className="h-4 w-4" />
                          )}
                          Refuser
                        </button>
                      </>
                    )}
                  </>
                )}
                <button
                  onClick={closePdfViewer}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* PDF Content */}
            <div className="flex-1 bg-gray-100">
              {loadingPdf ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-cyan-500 mx-auto mb-3" />
                    <p className="text-gray-500">Chargement du PDF...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0"
                  title={`PDF ${pdfInvoice?.invoiceNumber || ''}`}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Impossible de charger le PDF</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
